import React, { useEffect, useMemo, useState } from "react";

import { useListSoilSightScenarios, useTranslation } from "utils";

import { MAX_NAME_LENGTH, SCENARIO_LIMITS } from "consts";

import { SoilSightProject } from "models";

import { AlertBanner, Button, Flex, Form, Icon, Input, Option, Select, Typography } from "components";

import { getNameValidationError } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

type Phase = "choose" | "existing" | "name";

type ProjectEntryDialogProps = {
  open: boolean;
  projects: SoilSightProject[];
  activeProjectId?: string | null;
  onCreateNew: () => void;
  onAddToExisting: (soilSightProjectId: string, scenarioName: string) => void;
  onClose: () => void;
};

export const ProjectEntryDialog = ({ open, projects, activeProjectId, onCreateNew, onAddToExisting, onClose }: ProjectEntryDialogProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const [phase, setPhase] = useState<Phase>("choose");
  const [confirmedProjectId, setConfirmedProjectId] = useState<string | null>(null);
  const [selectForm] = Form.useForm();
  const [nameForm] = Form.useForm();

  const watchedProjectId = Form.useWatch("projectId", selectForm) as string | undefined;
  const watchedScenarioName = Form.useWatch("scenarioName", nameForm);
  // internalProjectId mirrors watchedProjectId via state so canConfirm is always correct even when
  // Form.useWatch hasn't yet re-rendered after setFieldsValue called before the Form mounts.
  const [internalProjectId, setInternalProjectId] = useState<string | null>(null);
  const effectiveProjectId = watchedProjectId ?? internalProjectId;
  const selectedProjectId = phase === "name" ? confirmedProjectId : effectiveProjectId;
  const selectedProject = projects.find((f) => f.id === selectedProjectId) ?? null;
  const isAtMaxScenarios = !!selectedProject && selectedProject.scenarioCount >= SCENARIO_LIMITS.MAX_SCENARIOS;
  const canConfirm = !!effectiveProjectId && !isAtMaxScenarios;

  const availableProjects = projects.filter((f) => f.scenarioCount < SCENARIO_LIMITS.MAX_SCENARIOS);
  const hasAvailableProjects = availableProjects.length > 0;

  // The chosen project's scenario names, so the imported scenario can't duplicate one; a failed or paused (offline) list falls back to the format checks.
  const {
    data: chosenProjectScenarios,
    isSuccess: areChosenProjectNamesLoaded,
    isError: didChosenProjectNamesFail,
    fetchStatus: chosenProjectNamesFetchStatus,
  } = useListSoilSightScenarios(phase === "name" ? confirmedProjectId ?? "" : "");
  const areChosenProjectNamesSettled = areChosenProjectNamesLoaded || didChosenProjectNamesFail || chosenProjectNamesFetchStatus === "paused";
  const chosenProjectScenarioNames = useMemo(
    () => new Set((chosenProjectScenarios ?? []).map((scenario) => (scenario.name ?? "").trim().toLowerCase())),
    [chosenProjectScenarios]
  );

  const getScenarioNameError = (value: string): string | null => getNameValidationError(value, t, chosenProjectScenarioNames);

  const scenarioNameRules = [
    {
      validator: async (_: unknown, value: string | undefined) => {
        const v = value ?? "";
        if (v === "") return Promise.reject(new Error(t("projectEntryDialog.scenarioNameRequired")));
        const err = getScenarioNameError(v);
        if (err) return Promise.reject(new Error(err));
        return Promise.resolve();
      },
    },
  ];

  const canConfirmName =
    phase === "name" &&
    areChosenProjectNamesSettled &&
    Boolean(String(watchedScenarioName ?? "").trim()) &&
    !getScenarioNameError(String(watchedScenarioName ?? ""));

  useEffect(() => {
    if (!open) {
      setPhase("choose");
      setConfirmedProjectId(null);
      setInternalProjectId(null);
      selectForm.resetFields();
      nameForm.resetFields();
    }
  }, [open, selectForm, nameForm]);

  const goToExisting = () => {
    const isActiveAvailable = !!activeProjectId && availableProjects.some((f) => f.id === activeProjectId);
    const defaultProjectId = isActiveAvailable ? activeProjectId : availableProjects[0]?.id ?? projects[0]?.id ?? null;
    selectForm.setFieldsValue({ projectId: defaultProjectId });
    setInternalProjectId(defaultProjectId ?? null);
    setPhase("existing");
  };

  const handleBackToChoose = () => {
    selectForm.resetFields();
    setConfirmedProjectId(null);
    setInternalProjectId(null);
    setPhase("choose");
  };

  const handleBackToExisting = () => {
    nameForm.resetFields();
    setPhase("existing");
  };

  const handleProceedToName = () => {
    if (!canConfirm) return;
    setConfirmedProjectId(effectiveProjectId ?? null);
    setPhase("name");
  };

  const handleConfirmName = async () => {
    // Enter submits too, so it must wait for the same checks as the button.
    if (!canConfirmName) return;
    try {
      const values = await nameForm.validateFields();
      const projectId = confirmedProjectId;
      if (projectId) onAddToExisting(projectId, values.scenarioName.trim());
    } catch {
      /* validation error — form shows inline message */
    }
  };

  if (!open) return null;

  return (
    <div className="scenario-analysis-modal-overlay" onClick={onClose}>
      <div className="scenario-analysis-modal themed" onClick={(e) => e.stopPropagation()}>
        <div className="scenario-analysis-modal-header">
          <div className="scenario-analysis-modal-header-text">
            {phase === "choose" && (
              <>
                <Typography variant="h5" className="scenario-analysis-modal-title">
                  {t("projectEntryDialog.title")}
                </Typography>
                <Typography variant="paragraph-s" color="grey-600" className="scenario-analysis-modal-subtitle">
                  {t("projectEntryDialog.chooseSubtitle")}
                </Typography>
              </>
            )}
            {phase === "existing" && (
              <>
                <Typography variant="h5" className="scenario-analysis-modal-title">
                  {t("projectEntryDialog.existingTitle")}
                </Typography>
                <Typography variant="paragraph-s" color="grey-600" className="scenario-analysis-modal-subtitle">
                  {t("projectEntryDialog.existingSubtitle")}
                </Typography>
              </>
            )}
            {phase === "name" && (
              <>
                <Typography variant="h5" className="scenario-analysis-modal-title">
                  {t("projectEntryDialog.nameTitle")}
                </Typography>
                <Typography variant="paragraph-s" color="grey-600" className="scenario-analysis-modal-subtitle">
                  {t("projectEntryDialog.nameSubtitle")}
                </Typography>
              </>
            )}
          </div>
          <Button
            className="scenario-analysis-modal-close"
            onClick={onClose}
            dataTestId="project-entry-dialog-close"
            aria-label={t("projectEntryDialog.close")}
          >
            <Icon iconName="tabler/x" size="m" />
          </Button>
        </div>

        {!hasAvailableProjects && (
          <div className="scenario-analysis-modal-alert-banner">
            <AlertBanner
              dataTestId="project-entry-dialog-no-projects-banner"
              closeable={false}
              type="info"
              iconName="tabler/info-circle"
              iconAlign="start"
              iconSize={16}
              descriptionSize="paragraph-s"
              description={t("projectEntryDialog.noProjectsBanner", { max: SCENARIO_LIMITS.MAX_SCENARIOS })}
            />
          </div>
        )}

        {phase === "choose" && (
          <div className="scenario-analysis-modal-content choose">
            <Button
              className="scenario-analysis-modal-option-card"
              onClick={goToExisting}
              disabled={!hasAvailableProjects}
              dataTestId="project-entry-dialog-option-existing"
            >
              <Icon iconName="tabler/folders" size="m" className="scenario-analysis-modal-option-card-icon" />
              <span className="scenario-analysis-modal-option-card-title">{t("projectEntryDialog.addToExisting")}</span>
              <span className="scenario-analysis-modal-option-card-desc">{t("projectEntryDialog.addToExistingDesc")}</span>
            </Button>
            <Button className="scenario-analysis-modal-option-card" onClick={onCreateNew} dataTestId="project-entry-dialog-option-create">
              <Icon iconName="klim/plus" size="m" className="scenario-analysis-modal-option-card-icon" />
              <span className="scenario-analysis-modal-option-card-title">{t("projectEntryDialog.createNew")}</span>
              <span className="scenario-analysis-modal-option-card-desc">{t("projectEntryDialog.createNewDesc")}</span>
            </Button>
          </div>
        )}

        {phase === "existing" && (
          <Form form={selectForm} validateTrigger={["onChange", "onBlur"]} className="scenario-analysis-modal-form">
            <div className="scenario-analysis-modal-content">
              <div className="scenario-analysis-modal-form-group">
                <div className="scenario-analysis-modal-input-group">
                  <Select
                    name="projectId"
                    label={t("projectEntryDialog.selectProject")}
                    allowClear={false}
                    className="scenario-analysis-modal-template-select"
                    dataTestId="project-entry-dialog-select"
                    value={selectedProjectId}
                  >
                    {projects.map((project) => (
                      <Option key={project.id} value={project.id} disabled={project.scenarioCount >= SCENARIO_LIMITS.MAX_SCENARIOS}>
                        {project.name ?? t("projectEntryDialog.unnamedProject")}
                      </Option>
                    ))}
                  </Select>
                </div>
                {isAtMaxScenarios && (
                  <Typography variant="paragraph-s" color="red-500">
                    {t("projectEntryDialog.maxScenariosReached", {
                      max: SCENARIO_LIMITS.MAX_SCENARIOS,
                    })}
                  </Typography>
                )}
              </div>
            </div>
          </Form>
        )}

        {phase === "name" && (
          <Form form={nameForm} validateTrigger={["onChange", "onBlur"]} className="scenario-analysis-modal-form">
            <div className="scenario-analysis-modal-content">
              <Input
                name="scenarioName"
                label={t("projectEntryDialog.scenarioNameLabel")}
                placeholder={t("projectEntryDialog.scenarioNamePlaceholder")}
                className="scenario-analysis-modal-title-input"
                maxLength={MAX_NAME_LENGTH + 1}
                rules={scenarioNameRules}
                onPressEnter={handleConfirmName}
              />
            </div>
          </Form>
        )}

        {phase === "existing" && (
          <div className="scenario-analysis-modal-footer">
            <Flex className="scenario-analysis-modal-footer-actions" justify="space-between" align="center" gap="1rem" style={{ width: "100%" }}>
              <Button type="text" onClick={handleBackToChoose} dataTestId="project-entry-dialog-back">
                {t("projectEntryDialog.back")}
              </Button>
              <Button
                type="primary"
                className="button-no-min-width auto-width"
                onClick={handleProceedToName}
                disabled={!canConfirm}
                style={{ maxWidth: "fit-content" }}
                dataTestId="project-entry-dialog-confirm"
              >
                {t("projectEntryDialog.next")}
              </Button>
            </Flex>
          </div>
        )}

        {phase === "name" && (
          <div className="scenario-analysis-modal-footer">
            <Flex className="scenario-analysis-modal-footer-actions" justify="space-between" align="center" gap="1rem" style={{ width: "100%" }}>
              <Button type="text" onClick={handleBackToExisting} dataTestId="project-entry-dialog-back-name">
                {t("projectEntryDialog.back")}
              </Button>
              <Button
                type="primary"
                className="button-no-min-width auto-width"
                onClick={handleConfirmName}
                disabled={!canConfirmName}
                style={{ maxWidth: "fit-content" }}
                dataTestId="project-entry-dialog-confirm-name"
              >
                {t("projectEntryDialog.create")}
              </Button>
            </Flex>
          </div>
        )}
      </div>
    </div>
  );
};
